import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { authService } from './AuthService'
import { isReactNativeApp } from '@amzn/jwo-store-management-platform-components'
import { EnvironmentDetail } from '@amzn/ihm-associate-tools-js-lib'

/**
 * Global authentication state and methods.
 * Available to any component via useAuth() hook.
 */
interface IAuthContextType {
    /** Is user currently authenticated? */
    isAuthenticated: boolean
    /** Is auth loading on app startup? */
    isAuthLoading: boolean
    /** Error message from last auth operation, null if no error */
    error: string | null
    /** Trigger logout */
    logout: () => Promise<void>
    /** Initialize auth on app start */
    initialize: () => Promise<void>
}

const AuthContext = createContext<IAuthContextType | undefined>(undefined)

/**
 * AuthProvider wraps the app to provide global auth state.
 * Native app handles login - WebView only checks status and requests logout.
 */
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false)
    const [isAuthLoading, setIsAuthLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    /**
     * Initialize auth on mount.
     * Checks authentication status by requesting token from native.
     */
    const initialize = async () => {
        console.log('[AuthContext] Initializing, isReactNativeApp:', isReactNativeApp(), 'ReactNativeWebView:', !!window.ReactNativeWebView, 'isAndroid:', EnvironmentDetail.isAndroid())
        setIsAuthLoading(true)
        try {
            await authService.initialize()
            const authenticated = await authService.isAuthenticated()
            setIsAuthenticated(authenticated)
            console.log('[AuthContext] Initialized, authenticated:', authenticated)
        } catch (err: any) {
            console.error('[AuthContext] Initialization failed:', err)
            setError(err.message)
        } finally {
            setIsAuthLoading(false)
        }
    }

    /**
     * Logout user via native app.
     */
    const logout = async () => {
        console.log('[AuthContext] Logout started')
        try {
            await authService.logout()
            setIsAuthenticated(false)
            console.log('[AuthContext] Logout successful')
        } catch (err: any) {
            console.error('[AuthContext] Logout failed:', err)
            throw err
        }
    }

    // Initialize on mount
    useEffect(() => {
        initialize()
    }, [])

    return (
        <AuthContext.Provider value={{ isAuthenticated, isAuthLoading, error, logout, initialize }}>
            {children}
        </AuthContext.Provider>
    )
}

/**
 * Hook to access auth state and methods.
 * Must be used within AuthProvider.
 *
 * @example
 * const { isAuthenticated, logout } = useAuth()
 */
export const useAuth = (): IAuthContextType => {
    const context = useContext(AuthContext)
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider')
    }
    return context
}
